// =====================================================================
// Enhanced Patch Compare V2 - Ultimate Patch Analysis Tool
// Features:
// 1. Hero Build Comparison - เทียบ Hero + Items ระหว่าง patches
// 2. Item Deep Dive - วิเคราะห์ Item changes แบบละเอียด
// 3. Meta Impact Analysis - Winners & Losers
// 4. Visual Diff Cards - UI สวยงาม
// 5. Gold Efficiency Calculator
// =====================================================================

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Zap, Sword, Shield, Heart, Wind, Users, TrendingUp, TrendingDown,
  GitCompare, Target, Calculator, Layers, ChevronRight, ChevronDown,
  Plus, X, RotateCcw, FileSpreadsheet, Package, Coins, Star, Award,
  AlertTriangle, CheckCircle, ArrowUpRight, ArrowDownRight, Minus,
  Eye, EyeOff, Filter, Search, BarChart3, Activity, Sparkles,
  Crown, Skull, Flame, Snowflake, Zap as Lightning
} from "lucide-react";
import {
  computeTotalStatsRealisticRealisticRealistic,
} from './statsCalculator';
// =====================================================================
// CONSTANTS & HELPERS
// =====================================================================
const UI = {
  radius: 14,
  radiusSm: 10,
  border: "rgba(255,16,38,0.22)",
  cardBg: "rgba(0,0,0,0.78)",
  glass: "blur(18px)",
  text: "#E5E7EB",
  sub: "rgba(229,231,235,0.65)",
  muted: "rgba(229,231,235,0.45)",
  neon: "rgba(255,16,38,0.35)",
  shadow: "0 18px 50px rgba(0,0,0,0.55)",
  shadowSoft: "0 10px 30px rgba(0,0,0,0.45)",
  mono: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
};

const DiffChip = ({ d }) => {
  const label = STAT_LABELS[d.field] || d.field;
  const isNum = typeof d.diff === "number";
  const isPos = isNum && d.diff > 0;
  const isNeg = isNum && d.diff < 0;

  const Icon = STAT_ICONS[d.field] || Activity;

  const isPct =
    d.field.includes("_pct") ||
    d.field === "attack_speed" ||
    d.field === "crit_rate" ||
    d.field === "life_steal" ||
    d.field === "cooldown_reduction";

  // ข้อความสั้น ๆ บนชิป
  let text = "";
  if (d.field === "passive") text = "Passive";
  else if (d.field === "role") text = "Role";
  else if (isNum) {
    const sign = d.diff > 0 ? "+" : "";
    text = `${sign}${Math.round(d.diff)}${isPct ? "%" : ""}`;
  } else {
    text = "Changed";
  }

  const bg =
    d.field === "passive"
      ? "rgba(139,92,246,0.14)"
      : isPos
      ? "rgba(34,197,94,0.14)"
      : isNeg
      ? "rgba(239,68,68,0.14)"
      : "rgba(255,16,38,0.10)";

  const bd =
    d.field === "passive"
      ? "rgba(139,92,246,0.35)"
      : isPos
      ? "rgba(34,197,94,0.35)"
      : isNeg
      ? "rgba(239,68,68,0.35)"
      : "rgba(255,16,38,0.25)";

  const fg =
    d.field === "passive"
      ? "#c4b5fd"
      : isPos
      ? "#22c55e"
      : isNeg
      ? "#ef4444"
      : "#ffd7dc";

  // tooltip ให้โชว์ละเอียด (old→new) แบบอ่านง่าย
  const oldV = typeof d.old === "number" ? Math.round(d.old) : d.old;
  const newV = typeof d.new === "number" ? Math.round(d.new) : d.new;

  const tip =
    d.field === "passive"
      ? "Passive changed"
      : d.field === "role"
      ? `Role: ${d.old} → ${d.new}`
      : `${label}: ${oldV} → ${newV}`;

  return (
    <Tooltip content={tip}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          borderRadius: 999,
          background: bg,
          border: `1px solid ${bd}`,
          color: fg,
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: "0.02em",
          lineHeight: 1,
          userSelect: "none",
          cursor: "help",
          whiteSpace: "nowrap",
        }}
      >
        <Icon size={12} color={fg} />
        <span style={{ opacity: 0.95 }}>{label}</span>
        <span style={{ color: "#fff", fontFamily: "monospace", fontWeight: 900 }}>
          {text}
        </span>
      </span>
    </Tooltip>
  );
};

const DiffChipsRow = ({ diffs = [], limit = 4 }) => {
  if (!diffs || diffs.length === 0) return null;

  // เรียงให้สำคัญก่อน
  const weight = (d) => {
    if (d.field === "price") return 5000 + Math.abs(d.diff || 0);
    if (d.field === "passive") return 4000;
    return Math.abs(d.diff || 0);
  };

  const sorted = [...diffs].sort((a, b) => weight(b) - weight(a));
  const shown = sorted.slice(0, limit);
  const more = sorted.length - shown.length;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      {shown.map((d, idx) => (
        <DiffChip key={`${d.field}-${idx}`} d={d} />
      ))}
      {more > 0 && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 8px",
            borderRadius: 999,
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,16,38,0.18)",
            color: "#9ca3af",
            fontSize: 10,
            fontWeight: 800,
            cursor: "default",
            userSelect: "none",
          }}
        >
          +{more} more
        </span>
      )}
    </div>
  );
};
const PrettyItemRow = ({
  tone = "buff", // "buff" | "nerf"
  name,
  category,
  diffs = [],
  itemObj,
  getItemImageCandidates,
}) => {
  const isBuff = tone === "buff";

  const accent = isBuff ? "#22c55e" : "#ef4444";
  const bg = isBuff ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)";
  const bd = isBuff ? "rgba(34,197,94,0.22)" : "rgba(239,68,68,0.22)";
  const glow = isBuff ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)";

  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        padding: "12px 12px",
        borderRadius: UI.radius,
        background: `linear-gradient(135deg, ${bg}, rgba(0,0,0,0.35))`,
        border: `1px solid ${bd}`,
        boxShadow: hover
          ? `0 18px 55px rgba(0,0,0,0.65), 0 0 0 1px ${bd}, 0 0 28px ${glow}`
          : UI.shadowSoft,
        backdropFilter: UI.glass,
        overflow: "hidden",
        transform: hover ? "translateY(-1px)" : "translateY(0)",
        transition: "all 220ms ease",
        cursor: "help",
      }}
    >
      {/* shine line */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.08) 35%, transparent 70%)",
          transform: hover ? "translateX(0)" : "translateX(-35%)",
          opacity: hover ? 1 : 0,
          transition: "all 320ms ease",
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      />

      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <ItemThumb
          item={itemObj || { name, category }}
          getItemImageCandidates={getItemImageCandidates}
          size={48}
          frame={true}
          style={{
            borderRadius: 14,
            boxShadow: hover ? `0 0 24px ${glow}` : "none",
            transition: "box-shadow 220ms ease",
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* header */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 950,
                  color: "#fff",
                  fontSize: 13,
                  lineHeight: 1.15,
                  letterSpacing: "0.02em",
                  textShadow: "0 2px 12px rgba(0,0,0,0.6)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {name}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                <Badge color="rgba(255,16,38,1)" size="xs">
                  {category}
                </Badge>

                <span style={{ color: UI.muted, fontSize: 10 }}>
                  {isBuff ? "Buffed" : "Nerfed"}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(0,0,0,0.35)",
                border: `1px solid ${bd}`,
                color: accent,
                fontWeight: 900,
                fontSize: 11,
                letterSpacing: "0.08em",
              }}
            >
              {isBuff ? "▲" : "▼"} {diffs?.length || 0}
            </div>
          </div>

          {/* chips */}
          <div style={{ marginTop: 8 }}>
            <DiffChipsRow diffs={diffs} limit={4} />
          </div>

          {/* tiny hint line */}
          <div
            style={{
              marginTop: 8,
              color: UI.muted,
              fontSize: 10,
              fontFamily: UI.mono,
              lineHeight: 1.25,
            }}
          >
            Hover for full details
          </div>
        </div>
      </div>
    </div>
  );
};

const formatDiffValue = (field, diff) => {
  const isPct =
    field.includes("_pct") ||
    field === "attack_speed" ||
    field === "crit_rate" ||
    field === "life_steal" ||
    field === "cooldown_reduction";

  const n = typeof diff === "number" ? diff : 0;
  const sign = n > 0 ? "+" : "";
  return `${sign}${Math.round(n)}${isPct ? "%" : ""}`;
};

const buildItemDiffTooltip = (diffs = []) => {
  if (!diffs || diffs.length === 0) return "No changes";

  const lines = diffs.map((d) => {
    if (d.field === "passive") return "Passive: changed";
    if (d.field === "role") return `Role: ${d.old} → ${d.new}`;

    const label = STAT_LABELS[d.field] || d.field;
    const oldV = typeof d.old === "number" ? Math.round(d.old) : d.old;
    const newV = typeof d.new === "number" ? Math.round(d.new) : d.new;

    if (d.field === "price") {
      return `Price: ${oldV} → ${newV} (${formatDiffValue(d.field, d.diff)})`;
    }

    return `${label}: ${oldV} → ${newV} (${formatDiffValue(d.field, d.diff)})`;
  });

  // จำกัดบรรทัดไม่ให้ยาวเกินไป (ปรับเลขได้)
  const trimmed = lines.slice(0, 6);
  const more = lines.length > 6 ? `\n…และอีก ${lines.length - 6} รายการ` : "";

  return trimmed.join("\n") + more;
};
const formatDiffShort = (d) => {
  const label = STAT_LABELS[d.field] || d.field;

  // Passive/Role
  if (d.field === "passive") return "Passive changed";
  if (d.field === "role") return `Role: ${d.old} → ${d.new}`;

  // numbers
  const oldV = typeof d.old === "number" ? Math.round(d.old) : d.old;
  const newV = typeof d.new === "number" ? Math.round(d.new) : d.new;

  // show "old→new (+x)"
  if (typeof d.diff === "number") {
    const sign = d.diff > 0 ? "+" : "";
    const isPct =
      d.field.includes("_pct") ||
      d.field === "attack_speed" ||
      d.field === "crit_rate" ||
      d.field === "life_steal" ||
      d.field === "cooldown_reduction";

    return `${label}: ${oldV}→${newV} (${sign}${Math.round(d.diff)}${isPct ? "%" : ""})`;
  }

  return `${label}: ${oldV}→${newV}`;
};

const getTopDiffLines = (diffs = [], limit = 3) => {
  if (!diffs || diffs.length === 0) return [];

  // เรียงให้ stat ที่สำคัญ/เปลี่ยนเยอะขึ้นก่อน
  const weight = (d) => {
    if (d.field === "price") return 5000 + Math.abs(d.diff || 0);
    if (d.field === "passive") return 4000;
    // stat: ใช้ abs(diff) เป็นหลัก
    return Math.abs(d.diff || 0);
  };

  return [...diffs]
    .sort((a, b) => weight(b) - weight(a))
    .slice(0, limit)
    .map(formatDiffShort);
};


const getNumericStatKeys = (obj) =>
  Object.keys(obj || {})
    .filter((k) => typeof obj[k] === "number" && obj[k] !== 0)
    .sort((a, b) => a.localeCompare(b));


const STAT_LABELS = {
  hp: "HP", phys_atk: "ATK", magic_power: "AP", phys_def: "Phys DEF",
  magic_def: "Magic DEF", move_speed: "Move Speed", max_mana: "Max Mana",
  armor_pierce: "Armor Pierce", armor_pierce_pct: "Armor Pierce %",
  magic_pierce: "Magic Pierce", magic_pierce_pct: "Magic Pierce %",
  attack_speed: "Attack Speed %", crit_rate: "Crit Rate %",
  crit_dmg: "Crit Damage %", life_steal: "Life Steal %",
  cooldown_reduction: "CDR %", hp_regen: "HP Regen", mana_regen: "Mana Regen",
  spell_vamp: "Spell Vamp %", price: "Price"
};

const STAT_ICONS = {
  hp: Heart, phys_atk: Sword, magic_power: Zap, phys_def: Shield,
  magic_def: Shield, move_speed: Wind, attack_speed: Activity,
  crit_rate: Target, life_steal: Heart, cooldown_reduction: RotateCcw,
  price: Coins
};

const STAT_COLORS = {
  hp: "#ef4444", phys_atk: "#f59e0b", magic_power: "#8b5cf6",
  phys_def: "#64748b", magic_def: "#06b6d4", move_speed: "#10b981",
  attack_speed: "#f97316", crit_rate: "#ec4899", life_steal: "#ef4444",
  cooldown_reduction: "#3b82f6", price: "#fbbf24"
};

const ROLES = {
  Fighter: { color: "#f59e0b", icon: Sword, gradient: "linear-gradient(135deg, #f59e0b, #d97706)" },
  Mage: { color: "#8b5cf6", icon: Zap, gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)" },
  Tank: { color: "#64748b", icon: Shield, gradient: "linear-gradient(135deg, #64748b, #475569)" },
  Support: { color: "#10b981", icon: Heart, gradient: "linear-gradient(135deg, #10b981, #059669)" },
  Assassin: { color: "#ef4444", icon: Target, gradient: "linear-gradient(135deg, #ef4444, #dc2626)" },
  Carry: { color: "#0ea5e9", icon: Target, gradient: "linear-gradient(135deg, #0ea5e9, #0284c7)" },
  Marksman: { color: "#f97316", icon: Target, gradient: "linear-gradient(135deg, #f97316, #ea580c)" },
};

const ITEM_CATEGORIES = {
  Attack: { color: "#ef4444", icon: Sword },
  Magic: { color: "#8b5cf6", icon: Zap },
  Defense: { color: "#64748b", icon: Shield },
  Movement: { color: "#10b981", icon: Wind },
  Jungle: { color: "#22c55e", icon: Target },
  Support: { color: "#0ea5e9", icon: Heart },
};

// Gold value per stat point (approximate)
const GOLD_VALUES = {
  hp: 2.67, phys_atk: 35, magic_power: 21.75, phys_def: 20,
  magic_def: 18, move_speed: 39.5, attack_speed: 25,
  crit_rate: 40, life_steal: 37.5, cooldown_reduction: 31.67,
  armor_pierce: 30, magic_pierce: 27
};

const getHeroLevelObj = (hero, level) =>
  hero?.levels?.[level] || hero?.levels?.[1] || Object.values(hero?.levels || {})[0] || null;

const sumItemStats = (items = []) => {
  const out = {};
  for (const it of items) {
    const s = it?.stats || {};
    for (const [k, v] of Object.entries(s)) out[k] = (out[k] || 0) + (+v || 0);
  }
  return out;
};

const computeTotalStatsRealisticRealistic = (heroLevelObj, items = []) => {
  const base = { ...(heroLevelObj || {}) };
  const bonus = sumItemStats(items);
  const total = { ...base };
  for (const [k, v] of Object.entries(bonus)) total[k] = (+total[k] || 0) + (+v || 0);
  return { base, bonus, total };
};

const calculateGoldEfficiency = (item) => {
  if (!item?.stats || !item?.price || item.price === 0) return null;
  let totalValue = 0;
  for (const [stat, value] of Object.entries(item.stats)) {
    if (GOLD_VALUES[stat]) {
      totalValue += Math.abs(value) * GOLD_VALUES[stat];
    }
  }
  return Math.round((totalValue / item.price) * 100);
};

// ---------- NEW: Asset helpers for item images (fallback if prop not provided) ----------
const normalizeAssetName = (s = "") =>
  String(s)
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const defaultGetItemImageCandidates = (itemOrName) => {
  const name = typeof itemOrName === "string" ? itemOrName : itemOrName?.name;
  if (!name) return [];
  const n = normalizeAssetName(name);

  // รองรับหลาย extension เผื่อไฟล์จริงไม่ตรงกัน
  return [
    `/itemimages/${n}.png`,
    `/itemimages/${n}.webp`,
    `/itemimages/${n}.jpg`,
    `/itemimages/${n}.jpeg`,
  ];
};

const toCssSize = (v) => (typeof v === "number" ? `${v}px` : v);

// =====================================================================
// UI COMPONENTS
// =====================================================================

const Card = ({ children, neon = false, glow = null, style = {}, onClick = null, hover = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: "rgba(0, 0, 0, 0.9)",
        backdropFilter: "blur(20px)",
        borderRadius: "16px",
        border: neon ? "2px solid #ff1026" : glow ? `2px solid ${glow}` : "1px solid rgba(255,16,38,0.2)",
        boxShadow: neon ? "0 0 30px rgba(255,16,38,0.4)" : glow ? `0 0 20px ${glow}40` : "none",
        position: "relative",
        transition: "all 0.3s ease",
        cursor: onClick ? "pointer" : "default",
        transform: hover && isHovered ? "translateY(-2px)" : "none",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const Button = ({ children, onClick, variant = "primary", style = {}, disabled = false, size = "md" }) => {
  const [hover, setHover] = useState(false);
  const sizes = { sm: { padding: "6px 12px", fontSize: "11px" }, md: { padding: "10px 20px", fontSize: "13px" }, lg: { padding: "14px 28px", fontSize: "15px" } };
  const variants = {
    primary: { background: hover && !disabled ? "linear-gradient(135deg, #ff4d5f, #ff1026)" : "linear-gradient(135deg, #ff1026, #a30013)", color: "#fff" },
    secondary: { background: hover && !disabled ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.6)", color: hover && !disabled ? "#fff" : "#9ca3af", border: hover && !disabled ? "2px solid #ff1026" : "2px solid rgba(255,16,38,0.3)" },
    danger: { background: hover && !disabled ? "rgba(239,68,68,0.3)" : "rgba(239,68,68,0.2)", color: "#ef4444", border: "2px solid rgba(239,68,68,0.5)" },
    success: { background: hover && !disabled ? "rgba(34,197,94,0.3)" : "rgba(34,197,94,0.2)", color: "#22c55e", border: "2px solid rgba(34,197,94,0.5)" },
    ghost: { background: "transparent", color: hover && !disabled ? "#ff1026" : "#6b7280", border: "none" },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...sizes[size],
        borderRadius: "12px",
        border: "none",
        fontWeight: "bold",
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "'Rajdhani', sans-serif",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
        transition: "all 0.3s",
        ...variants[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const Select = ({ value, onChange, options, style = {}, icon = null }) => (
  <div style={{ position: "relative", ...style }}>
    {icon && <div style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#ff1026", pointerEvents: "none" }}>{icon}</div>}
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: icon ? "10px 12px 10px 34px" : "10px 12px",
        background: "rgba(0,0,0,0.6)",
        border: "2px solid rgba(255,16,38,0.3)",
        borderRadius: "12px",
        color: "#fff",
        fontSize: "14px",
        fontFamily: "'Rajdhani', sans-serif",
        appearance: "none",
        cursor: "pointer",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ff1026'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 10px center",
        backgroundSize: "16px",
      }}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={o.value} style={{ background: "#000" }}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const Input = ({ value, onChange, placeholder, style = {}, icon = null }) => (
  <div style={{ position: "relative", ...style }}>
    {icon && <div style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#ff1026" }}>{icon}</div>}
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: icon ? "10px 12px 10px 38px" : "10px 12px",
        background: "rgba(0,0,0,0.6)",
        border: "2px solid rgba(255,16,38,0.3)",
        borderRadius: "12px",
        color: "#fff",
        fontSize: "14px",
        fontFamily: "'Rajdhani', sans-serif",
        outline: "none",
        transition: "border-color 0.3s",
      }}
    />
  </div>
);

const Badge = ({ children, color = "#ff1026", size = "sm" }) => {
  const sizes = { xs: { padding: "2px 6px", fontSize: "9px" }, sm: { padding: "3px 8px", fontSize: "10px" }, md: { padding: "4px 10px", fontSize: "11px" } };
  return (
    <span
      style={{
        ...sizes[size],
        borderRadius: "6px",
        background: `${color}20`,
        color: color,
        fontWeight: "bold",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      {children}
    </span>
  );
};

const DiffIndicator = ({ value, showZero = false, size = "md" }) => {
  if (value === 0 && !showZero) return null;
  const isPositive = value > 0;
  const Icon = isPositive ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const color = isPositive ? "#22c55e" : value < 0 ? "#ef4444" : "#6b7280";
  const sizes = { sm: 10, md: 12, lg: 14 };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", color, fontWeight: "bold", fontSize: sizes[size] }}>
      <Icon size={sizes[size]} />
      {isPositive ? "+" : ""}{typeof value === "number" ? Math.round(value).toLocaleString() : value}
    </span>
  );
};

const ProgressBar = ({ value, max, color = "#ff1026", height = 6, showLabel = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
    <div style={{ flex: 1, height, background: "rgba(0,0,0,0.6)", borderRadius: height / 2, overflow: "hidden" }}>
      <div
        style={{
          width: `${Math.min(100, (value / max) * 100)}%`,
          height: "100%",
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          borderRadius: height / 2,
          transition: "width 0.5s ease",
        }}
      />
    </div>
    {showLabel && <span style={{ fontSize: "11px", color: "#6b7280", minWidth: "40px" }}>{Math.round(value)}</span>}
  </div>
);

const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}

      {show && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: 10,
            padding: "10px 12px",
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.92), rgba(0,0,0,0.92))",
            border: "1px solid rgba(255,16,38,0.28)",
            borderRadius: 12,
            color: "#fff",
            fontSize: 11,
            lineHeight: 1.35,
            whiteSpace: "pre-wrap",
            maxWidth: 360,
            textAlign: "left",
            zIndex: 999,
            boxShadow: "0 18px 60px rgba(0,0,0,0.75)",
            backdropFilter: UI.glass,
          }}
        >
          {/* title line (optional-ish but makes it feel premium) */}
          <div style={{ fontWeight: 900, letterSpacing: "0.04em", color: "#ffd7dc", marginBottom: 6 }}>
            Changes
          </div>
          <div style={{ color: "rgba(255,255,255,0.92)" }}>{content}</div>

          {/* little arrow */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "100%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "8px solid rgba(0,0,0,0.92)",
              filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.7))",
            }}
          />
        </div>
      )}
    </div>
  );
};


const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    style={{
      padding: "12px 20px",
      background: active ? "linear-gradient(135deg, rgba(255,16,38,0.3), rgba(255,16,38,0.1))" : "transparent",
      border: active ? "2px solid #ff1026" : "2px solid transparent",
      borderRadius: "12px",
      color: active ? "#ff1026" : "#6b7280",
      fontWeight: "bold",
      fontSize: "13px",
      fontFamily: "'Rajdhani', sans-serif",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s",
    }}
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);

// =====================================================================
// NEW: THUMBNAILS (Hero / Item) with fallback
// =====================================================================

const HeroThumb = ({ name, role, getHeroImageUrl, size = 48, style = {} }) => {
  const [broken, setBroken] = useState(false);
  const RoleIcon = ROLES[role]?.icon || Users;
  const roleColor = ROLES[role]?.color || "#6b7280";
  const src = !broken && getHeroImageUrl ? getHeroImageUrl(name) : null;

  return (
    <div
      style={{
        width: toCssSize(size),
        height: toCssSize(size),
        borderRadius: 10,
        overflow: "hidden",
        border: `2px solid ${roleColor}55`,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={name || ""}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setBroken(true)}
        />
      ) : (
        <RoleIcon size={24} color={roleColor} />
      )}
    </div>
  );
};

const ItemThumb = ({ item, getItemImageCandidates, size = 40, frame = true, style = {} }) => {
  const [idx, setIdx] = useState(0);

  const candidates = useMemo(() => {
    const fromProp = getItemImageCandidates ? getItemImageCandidates(item) : [];
    const fromPropClean = Array.isArray(fromProp) ? fromProp.filter(Boolean) : [];
    const fallback = defaultGetItemImageCandidates(item);
    return [...fromPropClean, ...fallback];
  }, [item, getItemImageCandidates]);

  const src = candidates[idx] || null;

  const cat = item?.category;
  const catColor = ITEM_CATEGORIES[cat]?.color || "#ff1026";
  const Icon = ITEM_CATEGORIES[cat]?.icon || Package;

  return (
    <div
      style={{
        width: toCssSize(size),
        height: toCssSize(size),
        borderRadius: 10,
        overflow: "hidden",
        border: frame ? `2px solid ${catColor}55` : "none",
        background: frame ? "rgba(0,0,0,0.6)" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={item?.name || ""}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setIdx((p) => (p + 1 < candidates.length ? p + 1 : p + 1))}
        />
      ) : (
        <Icon size={20} color={catColor} />
      )}
    </div>
  );
};

// =====================================================================
// STAT DIFF CARD - แสดง stat change สวยงาม
// =====================================================================

const StatDiffCard = ({ stat, oldValue, newValue, compact = false }) => {
  const diff = newValue - oldValue;
  const pctChange = oldValue !== 0 ? ((diff / oldValue) * 100).toFixed(1) : "∞";
  const Icon = STAT_ICONS[stat] || Activity;
  const color = STAT_COLORS[stat] || "#ff1026";

  if (compact) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: diff > 0 ? "rgba(34,197,94,0.1)" : diff < 0 ? "rgba(239,68,68,0.1)" : "rgba(107,114,128,0.1)",
          borderRadius: "8px",
          border: `1px solid ${diff > 0 ? "rgba(34,197,94,0.3)" : diff < 0 ? "rgba(239,68,68,0.3)" : "rgba(107,114,128,0.3)"}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Icon size={14} color={color} />
          <span style={{ color: "#fff", fontSize: "12px", fontWeight: "bold" }}>{STAT_LABELS[stat] || stat}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ color: "#6b7280", fontSize: "11px" }}>{Math.round(oldValue)} → {Math.round(newValue)}</span>
          <DiffIndicator value={diff} showZero />
        </div>
      </div>
    );
  }

  return (
    <Card style={{ padding: "12px" }} glow={diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : null}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: `${color}20`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={18} color={color} />
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: "bold", fontSize: "13px" }}>{STAT_LABELS[stat] || stat}</div>
            <div style={{ color: "#6b7280", fontSize: "10px" }}>{pctChange}% change</div>
          </div>
        </div>
        <DiffIndicator value={diff} showZero size="lg" />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#6b7280", fontSize: "10px", marginBottom: "2px" }}>Before</div>
          <div style={{ color: "#fff", fontWeight: "bold", fontFamily: "monospace" }}>{Math.round(oldValue).toLocaleString()}</div>
        </div>
        <ChevronRight size={16} color="#ff1026" />
        <div style={{ flex: 1, textAlign: "right" }}>
          <div style={{ color: "#6b7280", fontSize: "10px", marginBottom: "2px" }}>After</div>
          <div style={{ color: diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#fff", fontWeight: "bold", fontFamily: "monospace" }}>
            {Math.round(newValue).toLocaleString()}
          </div>
        </div>
      </div>
    </Card>
  );
};

// =====================================================================
// HERO BUILD CARD - แสดง Hero + Items
// =====================================================================

const HeroBuildCard = ({
  hero,
  items = [],
  level = 15,
  patch,
  patchLabel,
  getHeroImageUrl,
  getItemImageCandidates,
  color = "#ff1026"
}) => {
  const heroLevelObj = getHeroLevelObj(hero, level);
  const { base, bonus, total } = computeTotalStatsRealisticRealistic(heroLevelObj, items);
  const totalCost = items.reduce((sum, it) => sum + (it?.price || 0), 0);
  const RoleIcon = ROLES[hero?.role]?.icon || Users;

  const heroImg = useMemo(() => (getHeroImageUrl ? getHeroImageUrl(hero?.name) : null), [getHeroImageUrl, hero?.name]);

  return (
    <Card style={{ padding: "16px" }} glow={color}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <div
          style={{
            width: "60px",
            height: "60px",
            borderRadius: "12px",
            overflow: "hidden",
            border: `3px solid ${color}`,
            background: "rgba(0,0,0,0.6)",
          }}
        >
          {heroImg ? (
            <img src={heroImg} alt={hero?.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Users size={24} color={color} />
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "16px", color }}>{hero?.name || "Unknown"}</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
            <Badge color={ROLES[hero?.role]?.color || "#6b7280"}>
              <RoleIcon size={10} />
              {hero?.role || "Unknown"}
            </Badge>
            <Badge color="#6b7280">Lv {level}</Badge>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: "#6b7280", fontSize: "10px" }}>Patch</div>
          <div style={{ color, fontWeight: "bold", fontSize: "12px" }}>{patchLabel}</div>
        </div>
      </div>

      {/* Items */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ color: "#6b7280", fontSize: "10px", fontWeight: "bold", marginBottom: "8px" }}>
          BUILD ({items.length}/6) • <Coins size={10} style={{ verticalAlign: "middle" }} /> {totalCost.toLocaleString()}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "6px" }}>
          {[...Array(6)].map((_, i) => {
            const item = items[i];
            return (
              <Tooltip key={i} content={item ? `${item.name} (${item.price || 0}g)` : "Empty slot"}>
                <div
                  style={{
                    aspectRatio: "1",
                    borderRadius: "8px",
                    background: item ? "rgba(255,16,38,0.15)" : "rgba(0,0,0,0.4)",
                    border: `2px solid ${item ? "rgba(255,16,38,0.4)" : "rgba(255,255,255,0.1)"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {item ? (
                    <ItemThumb
                      item={item}
                      getItemImageCandidates={getItemImageCandidates}
                      size="100%"
                      frame={false}
                      style={{ borderRadius: 0 }}
                    />
                  ) : (
                    <Plus size={12} color="#6b7280" />
                  )}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "6px" }}>
        {["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed"].map((stat) => {
          const baseVal = base[stat] || 0;
          const bonusVal = bonus[stat] || 0;
          const totalVal = total[stat] || 0;
          const Icon = STAT_ICONS[stat] || Activity;
          const statColor = STAT_COLORS[stat] || "#fff";

          return (
            <div
              key={stat}
              style={{
                padding: "8px",
                borderRadius: "8px",
                background: "rgba(0,0,0,0.4)",
                border: "1px solid rgba(255,16,38,0.1)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                <Icon size={12} color={statColor} />
                <span style={{ color: "#6b7280", fontSize: "10px" }}>{STAT_LABELS[stat] || stat}</span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                <span style={{ color: "#fff", fontWeight: "bold", fontSize: "14px", fontFamily: "monospace" }}>
                  {Math.round(totalVal).toLocaleString()}
                </span>
                {bonusVal !== 0 && (
                  <span style={{ color: "#22c55e", fontSize: "10px" }}>+{Math.round(bonusVal)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// =====================================================================
// ITEM COMPARISON CARD - เทียบ Item 2 patches
// =====================================================================

const ItemComparisonCard = ({ itemA, itemB, expanded = false, onToggle, getItemImageCandidates }) => {
  const name = itemB?.name || itemA?.name || "Unknown";
  const isNew = !itemA && itemB;
  const isRemoved = itemA && !itemB;
  const isChanged = itemA && itemB;

  const statsA = itemA?.stats || {};
  const statsB = itemB?.stats || {};
  const priceA = itemA?.price || 0;
  const priceB = itemB?.price || 0;

  const allStats = [...new Set([...Object.keys(statsA), ...Object.keys(statsB)])];
  const changedStats = allStats.filter((s) => (statsA[s] || 0) !== (statsB[s] || 0));

  const efficiencyA = calculateGoldEfficiency(itemA);
  const efficiencyB = calculateGoldEfficiency(itemB);

  const passiveChanged = itemA?.passive !== itemB?.passive;

  const borderColor = isNew ? "#22c55e" : isRemoved ? "#ef4444" : changedStats.length > 0 || priceA !== priceB ? "#ff1026" : "rgba(255,16,38,0.2)";

  const itemForThumb = itemB || itemA;
  const cat = itemForThumb?.category;
  const catColor = ITEM_CATEGORIES[cat]?.color || "#ff1026";

  return (
    <Card style={{ overflow: "hidden" }} glow={changedStats.length > 0 ? borderColor : null}>
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          background: isNew ? "rgba(34,197,94,0.1)" : isRemoved ? "rgba(239,68,68,0.1)" : "transparent",
          borderBottom: expanded ? "1px solid rgba(255,16,38,0.2)" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* NEW: Item image thumb */}
          <ItemThumb
            item={itemForThumb}
            getItemImageCandidates={getItemImageCandidates}
            size={40}
            frame={true}
            style={{ borderColor: `${catColor}66` }}
          />

          <div>
            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#fff" }}>{name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "2px" }}>
              <Badge color={catColor} size="xs">
                {itemB?.category || itemA?.category || "Unknown"}
              </Badge>
              {isNew && <Badge color="#22c55e" size="xs"><Plus size={8} /> NEW</Badge>}
              {isRemoved && <Badge color="#ef4444" size="xs"><X size={8} /> REMOVED</Badge>}
              {isChanged && changedStats.length > 0 && <Badge color="#ff1026" size="xs">{changedStats.length} changes</Badge>}
              {passiveChanged && <Badge color="#8b5cf6" size="xs"><Sparkles size={8} /> Passive</Badge>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* Price */}
          <div style={{ textAlign: "right" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-end" }}>
              <Coins size={12} color="#fbbf24" />
              <span style={{ color: "#fbbf24", fontWeight: "bold", fontSize: "13px" }}>{priceB.toLocaleString()}</span>
              {priceA !== priceB && <DiffIndicator value={priceB - priceA} size="sm" />}
            </div>
            {efficiencyB !== null && (
              <div style={{ fontSize: "10px", color: efficiencyB >= 100 ? "#22c55e" : "#6b7280" }}>
                {efficiencyB}% efficient
              </div>
            )}
          </div>
          <ChevronDown
            size={16}
            color="#6b7280"
            style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.3s" }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: "16px" }}>
          {/* Stats Comparison */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ color: "#6b7280", fontSize: "11px", fontWeight: "bold", marginBottom: "8px" }}>STATS COMPARISON</div>
            <div style={{ display: "grid", gap: "6px" }}>
              {allStats.length === 0 ? (
                <div style={{ color: "#6b7280", fontSize: "12px", textAlign: "center", padding: "16px" }}>No stats</div>
              ) : (
                allStats.map((stat) => {
                  const valA = statsA[stat] || 0;
                  const valB = statsB[stat] || 0;
                  return <StatDiffCard key={stat} stat={stat} oldValue={valA} newValue={valB} compact />;
                })
              )}
            </div>
          </div>

          {/* Passive Comparison */}
          {(itemA?.passive || itemB?.passive) && (
            <div>
              <div style={{ color: "#6b7280", fontSize: "11px", fontWeight: "bold", marginBottom: "8px" }}>PASSIVE</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <div style={{ color: "#ef4444", fontSize: "10px", marginBottom: "4px" }}>Before</div>
                  <div
                    style={{
                      padding: "10px",
                      borderRadius: "8px",
                      background: "rgba(239,68,68,0.1)",
                      border: "1px solid rgba(239,68,68,0.2)",
                      fontSize: "11px",
                      color: "#9ca3af",
                      minHeight: "60px",
                    }}
                  >
                    {itemA?.passive || "N/A"}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#22c55e", fontSize: "10px", marginBottom: "4px" }}>After</div>
                  <div
                    style={{
                      padding: "10px",
                      borderRadius: "8px",
                      background: "rgba(34,197,94,0.1)",
                      border: "1px solid rgba(34,197,94,0.2)",
                      fontSize: "11px",
                      color: "#fff",
                      minHeight: "60px",
                    }}
                  >
                    {itemB?.passive || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Gold Efficiency */}
          {(efficiencyA !== null || efficiencyB !== null) && (
            <div style={{ marginTop: "16px", padding: "12px", background: "rgba(251,191,36,0.1)", borderRadius: "8px", border: "1px solid rgba(251,191,36,0.2)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Coins size={14} color="#fbbf24" />
                  <span style={{ color: "#fbbf24", fontWeight: "bold", fontSize: "12px" }}>Gold Efficiency</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <span style={{ color: "#6b7280", fontSize: "12px" }}>{efficiencyA ?? "N/A"}%</span>
                  <ChevronRight size={14} color="#ff1026" />
                  <span
                    style={{
                      color: efficiencyB !== null && efficiencyB >= (efficiencyA || 0) ? "#22c55e" : "#ef4444",
                      fontWeight: "bold",
                      fontSize: "12px",
                    }}
                  >
                    {efficiencyB ?? "N/A"}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// =====================================================================
// HERO COMPARISON CARD - เทียบ Hero 2 patches
// =====================================================================

const HeroComparisonCard = ({ heroA, heroB, level, expanded = false, onToggle, getHeroImageUrl }) => {
  const name = heroB?.name || heroA?.name || "Unknown";
  const isNew = !heroA && heroB;
  const isRemoved = heroA && !heroB;

  const lvlA = getHeroLevelObj(heroA, level) || {};
  const lvlB = getHeroLevelObj(heroB, level) || {};

  const statKeys = ["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed", "armor_pierce_pct", "attack_speed", "crit_rate", "life_steal", "cooldown_reduction"];
  const changedStats = statKeys.filter((s) => (lvlA[s] || 0) !== (lvlB[s] || 0));
  const roleChanged = heroA?.role !== heroB?.role;

  // Calculate buff/nerf score
  let buffScore = 0;
  changedStats.forEach((s) => {
    const diff = (lvlB[s] || 0) - (lvlA[s] || 0);
    buffScore += diff > 0 ? 1 : diff < 0 ? -1 : 0;
  });

  const roleNow = heroB?.role || heroA?.role;
  const RoleIcon = ROLES[roleNow]?.icon || Users;
  const roleColor = ROLES[roleNow]?.color || "#6b7280";

  return (
    <Card style={{ overflow: "hidden" }} glow={isNew ? "#22c55e" : isRemoved ? "#ef4444" : buffScore > 0 ? "#22c55e" : buffScore < 0 ? "#ef4444" : null}>
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          background: isNew ? "rgba(34,197,94,0.1)" : isRemoved ? "rgba(239,68,68,0.1)" : buffScore > 0 ? "rgba(34,197,94,0.05)" : buffScore < 0 ? "rgba(239,68,68,0.05)" : "transparent",
          borderBottom: expanded ? "1px solid rgba(255,16,38,0.2)" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* NEW: Hero image thumb */}
          <HeroThumb
            name={name}
            role={roleNow}
            getHeroImageUrl={getHeroImageUrl}
            size={48}
            style={{ borderColor: `${roleColor}66` }}
          />

          <div>
            <div style={{ fontWeight: "bold", fontSize: "15px", color: "#fff" }}>{name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
              <Badge color={roleColor} size="sm">
                <RoleIcon size={10} />
                {roleNow || "Unknown"}
              </Badge>
              {isNew && <Badge color="#22c55e" size="sm"><Plus size={8} /> NEW</Badge>}
              {isRemoved && <Badge color="#ef4444" size="sm"><X size={8} /> REMOVED</Badge>}
              {roleChanged && <Badge color="#8b5cf6" size="sm">Role Changed</Badge>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* Buff/Nerf Indicator */}
          {!isNew && !isRemoved && changedStats.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {buffScore > 0 && (
                <Badge color="#22c55e" size="md">
                  <TrendingUp size={12} />
                  BUFFED
                </Badge>
              )}
              {buffScore < 0 && (
                <Badge color="#ef4444" size="md">
                  <TrendingDown size={12} />
                  NERFED
                </Badge>
              )}
              {buffScore === 0 && changedStats.length > 0 && (
                <Badge color="#ff1026" size="md">
                  <Activity size={12} />
                  ADJUSTED
                </Badge>
              )}
              <span style={{ color: "#6b7280", fontSize: "11px" }}>{changedStats.length} stats</span>
            </div>
          )}
          <ChevronDown
            size={16}
            color="#6b7280"
            style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.3s" }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: "16px" }}>
          <div style={{ color: "#6b7280", fontSize: "11px", fontWeight: "bold", marginBottom: "8px" }}>STAT CHANGES (Level {level})</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "8px" }}>
            {statKeys.map((stat) => {
              const valA = lvlA[stat] || 0;
              const valB = lvlB[stat] || 0;
              if (valA === 0 && valB === 0) return null;
              return <StatDiffCard key={stat} stat={stat} oldValue={valA} newValue={valB} compact />;
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

// =====================================================================
// WINNERS & LOSERS SECTION
// =====================================================================

const WinnersLosersSection = ({
  heroDiff,
  itemDiff,
  level,
  getHeroImageUrl,
  getItemImageCandidates,
}) => {
  // Calculate hero scores
  const heroScores = useMemo(() => {
    return heroDiff.changed.map((h) => {
      let score = 0;
      h.diffs.forEach((d) => {
        if (typeof d.diff === "number") {
          // Normalize by stat importance
          const weight = d.field === "hp" ? 0.01 : d.field.includes("atk") || d.field.includes("power") ? 1 : 0.5;
          score += d.diff * weight;
        }
      });
      return { name: h.name, role: h.role, score, diffs: h.diffs };
    }).sort((a, b) => b.score - a.score);
  }, [heroDiff]);

  const winners = heroScores.filter((h) => h.score > 5).slice(0, 5);
  const losers = heroScores.filter((h) => h.score < -5).slice(0, 5).reverse();

  // Calculate item scores
const itemScores = useMemo(() => {
  return itemDiff.changed
    .map((it) => {
      let score = 0;
      it.diffs.forEach((d) => {
        if (typeof d.diff === "number" && d.field !== "price") score += d.diff;
        if (d.field === "price") score -= d.diff * 0.01;
      });

      return {
        name: it.name,
        category: it.category,
        score,
        diffs: it.diffs,
        item: it.itemB || it.itemA, // ✅ เอา object ไปทำรูป
      };
    })
    .sort((a, b) => b.score - a.score);
}, [itemDiff]);


  const buffedItems = itemScores.filter((it) => it.score > 0).slice(0, 5);
  const nerfedItems = itemScores.filter((it) => it.score < 0).slice(0, 5).reverse();

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Heroes */}
      <Card neon style={{ padding: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
          <Crown size={20} color="#fbbf24" />
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "16px" }}>OVERALL - HEROES</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          {/* Winners */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <TrendingUp size={16} color="#22c55e" />
              <span style={{ color: "#22c55e", fontWeight: "bold", fontSize: "13px" }}>WINNERS</span>
            </div>
            {winners.length === 0 ? (
              <div style={{ color: "#6b7280", fontSize: "12px", textAlign: "center", padding: "20px" }}>No significant buffs</div>
            ) : (
              <div style={{ display: "grid", gap: "8px" }}>
                {winners.map((h, i) => (
                  <Card
                    key={h.name}
                    style={{ padding: "12px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
  <HeroThumb
    name={h.name}
    role={h.role}
    getHeroImageUrl={getHeroImageUrl}
    size={48} // ✅ ชัดขึ้น
    style={{ borderRadius: 12 }}
  />

  <div>
    <div style={{ fontWeight: "bold", color: "#fff", fontSize: "13px" }}>{h.name}</div>
    <div style={{ color: "#6b7280", fontSize: "10px" }}>{h.role}</div>
  </div>

</div>

                      <Badge color="#22c55e">+{Math.round(h.score)}</Badge>
                    </div>
                    <div style={{ marginTop: "8px", fontSize: "10px", color: "#22c55e" }}>
                      {h.diffs
                        .filter((d) => typeof d.diff === "number" && d.diff > 0)
                        .slice(0, 3)
                        .map((d) => `${STAT_LABELS[d.field] || d.field} +${Math.round(d.diff)}`)
                        .join(", ")}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Losers */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <TrendingDown size={16} color="#ef4444" />
              <span style={{ color: "#ef4444", fontWeight: "bold", fontSize: "13px" }}>LOSERS</span>
            </div>
            {losers.length === 0 ? (
              <div style={{ color: "#6b7280", fontSize: "12px", textAlign: "center", padding: "20px" }}>No significant nerfs</div>
            ) : (
              <div style={{ display: "grid", gap: "8px" }}>
                {losers.map((h, i) => (
                  <Card
                    key={h.name}
                    style={{ padding: "12px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            background: "rgba(239,68,68,0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Skull size={12} color="#ef4444" />
                        </div>
                        <div>
                          <div style={{ fontWeight: "bold", color: "#fff", fontSize: "13px" }}>{h.name}</div>
                          <div style={{ color: "#6b7280", fontSize: "10px" }}>{h.role}</div>
                        </div>
                      </div>
                      <Badge color="#ef4444">{Math.round(h.score)}</Badge>
                    </div>
                    <div style={{ marginTop: "8px", fontSize: "10px", color: "#ef4444" }}>
                      {h.diffs
                        .filter((d) => typeof d.diff === "number" && d.diff < 0)
                        .slice(0, 3)
                        .map((d) => `${STAT_LABELS[d.field] || d.field} ${Math.round(d.diff)}`)
                        .join(", ")}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Items */}
      <Card style={{ padding: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
          <Package size={20} color="#ff1026" />
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "16px" }}>OVERALL - ITEMS</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          {/* Buffed Items */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <Flame size={16} color="#22c55e" />
              <span style={{ color: "#22c55e", fontWeight: "bold", fontSize: "13px" }}>BUFFED ITEMS</span>
            </div>
            {buffedItems.length === 0 ? (
              <div style={{ color: "#6b7280", fontSize: "12px", textAlign: "center", padding: "20px" }}>No item buffs</div>
            ) : (
              <div style={{ display: "grid", gap: "6px" }}>
{buffedItems.map((it) => (
  <Tooltip key={it.name} content={buildItemDiffTooltip(it.diffs)}>
    <PrettyItemRow
      tone="buff"
      name={it.name}
      category={it.category}
      diffs={it.diffs}
      itemObj={it.item || it.itemB || it.itemA}
      getItemImageCandidates={getItemImageCandidates}
    />
  </Tooltip>
))}



              </div>
            )}
          </div>

          {/* Nerfed Items */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <Snowflake size={16} color="#ef4444" />
              <span style={{ color: "#ef4444", fontWeight: "bold", fontSize: "13px" }}>NERFED ITEMS</span>
            </div>
            {nerfedItems.length === 0 ? (
  <div style={{ color: "#6b7280", fontSize: "12px", textAlign: "center", padding: "20px" }}>
    No item nerfs
  </div>
) : (
  <div style={{ display: "grid", gap: "6px" }}>
   {nerfedItems.map((it) => (
  <Tooltip key={it.name} content={buildItemDiffTooltip(it.diffs)}>
    <PrettyItemRow
      tone="nerf"
      name={it.name}
      category={it.category}
      diffs={it.diffs}
      itemObj={it.item || it.itemB || it.itemA}
      getItemImageCandidates={getItemImageCandidates}
    />
  </Tooltip>
))}


  </div>
)}

          </div>
        </div>
      </Card>
    </div>
  );
};

// =====================================================================
// HERO BUILD COMPARISON TAB
// =====================================================================
const HeroImageComboBox = ({
  heroes = [],
  value,
  onPick,                 // onPick(name)
  placeholder = "Select Hero...",
  getHeroImageUrl,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = useMemo(
    () => heroes.find((h) => h.name === value) || null,
    [heroes, value]
  );

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return heroes;
    return heroes.filter((h) => (h.name || "").toLowerCase().includes(k));
  }, [heroes, q]);

  const roleNow = selected?.role || "Unknown";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          width: "100%",
          padding: "10px 12px",
          background: "rgba(0,0,0,0.6)",
          border: "2px solid rgba(255,16,38,0.3)",
          borderRadius: "12px",
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <HeroThumb
          name={selected?.name || ""}
          role={roleNow}
          getHeroImageUrl={getHeroImageUrl}
          size={36}
        />
        <div style={{ flex: 1, textAlign: "left", fontWeight: "bold" }}>
          {selected?.name || <span style={{ color: "#6b7280" }}>{placeholder}</span>}
        </div>
        <ChevronDown size={16} color="#ff1026" style={{ opacity: 0.9 }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 999,
            left: 0,
            right: 0,
            marginTop: 8,
            background: "rgba(0,0,0,0.95)",
            border: "2px solid rgba(255,16,38,0.35)",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 0 30px rgba(255,16,38,0.2)",
          }}
        >
          <div style={{ padding: 10 }}>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search hero..."
              icon={<Search size={14} />}
            />
          </div>

          <div className="scroll-container" style={{ maxHeight: 360, overflow: "auto" }}>
            {filtered.map((h) => {
              const role = h.role || "Unknown";
              const roleColor = ROLES[role]?.color || "#6b7280";
              return (
                <button
                  key={h.name}
                  type="button"
                  onClick={() => {
                    onPick(h.name);
                    setOpen(false);
                    setQ("");
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    background: h.name === value ? "rgba(255,16,38,0.15)" : "transparent",
                    border: "none",
                    borderTop: "1px solid rgba(255,16,38,0.12)",
                    cursor: "pointer",
                    color: "#fff",
                    textAlign: "left",
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                >
                  <HeroThumb
                    name={h.name}
                    role={role}
                    getHeroImageUrl={getHeroImageUrl}
                    size={34}
                    style={{ borderColor: `${roleColor}66` }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "bold" }}>{h.name}</div>
                    <div style={{ color: "#6b7280", fontSize: 12 }}>{role}</div>
                  </div>
                </button>
              );
            })}

            {filtered.length === 0 && (
              <div style={{ padding: 12, color: "#6b7280" }}>No hero found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const BuildItemShop = ({
  itemsA = [],
  itemsB = [],
  selected = [],
  setSelected,
  getItemImageCandidates,
}) => {
  const [cat, setCat] = useState("Attack");
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(null);
  const maxSlots = 6;

  // รวม item จาก A+B (ให้ของ patch ใหม่ B ทับ A ถ้าชื่อซ้ำ)
  const mergedItems = useMemo(() => {
    const mapA = new Map(itemsA.map((it) => [it.name, it]));
    const mapB = new Map(itemsB.map((it) => [it.name, it]));
    const names = new Set([...mapA.keys(), ...mapB.keys()]);
    return [...names].map((name) => mapB.get(name) || mapA.get(name)).filter(Boolean);
  }, [itemsA, itemsB]);

  // สถานะไอเท็ม (ไว้ทำ badge)
  const statusMap = useMemo(() => {
    const mapA = new Map(itemsA.map((it) => [it.name, it]));
    const mapB = new Map(itemsB.map((it) => [it.name, it]));
    const out = new Map();
    const names = new Set([...mapA.keys(), ...mapB.keys()]);
    for (const name of names) {
      const a = mapA.get(name);
      const b = mapB.get(name);
      if (!a && b) out.set(name, "NEW");
      else if (a && !b) out.set(name, "REMOVED");
      else if (a && b) {
        const changed =
          (a.price || 0) !== (b.price || 0) ||
          JSON.stringify(a.stats || {}) !== JSON.stringify(b.stats || {}) ||
          (a.passive || "") !== (b.passive || "");
        if (changed) out.set(name, "CHANGED");
      }
    }
    return out;
  }, [itemsA, itemsB]);

  const cats = ["Attack", "Magic", "Defense", "Movement", "Jungling", "Support"];

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    return mergedItems
      .filter((it) => {
        const c = (it.category || "Unknown").trim();
        if (cat === "Jungling") return c === "Jungling" || c === "Jungle";
        return c === cat;
      })
      .filter((it) => (!k ? true : (it.name || "").toLowerCase().includes(k)));
  }, [mergedItems, cat, q]);

  const byTier = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const t = Number.isFinite(+it.tier) ? +it.tier : 1;
      if (!map.has(t)) map.set(t, []);
      map.get(t).push(it);
    }
    const tiers = [...map.keys()].sort((a, b) => a - b);
    return tiers.map((t) => ({
      tier: t,
      items: map.get(t).sort((a, b) => (+a.price || 0) - (+b.price || 0)),
    }));
  }, [filtered]);

  const totalPrice = selected.reduce((s, it) => s + (+it.price || 0), 0);

const addToBuild = (it) => {
  if (!it) return;
  if (selected.length >= maxSlots) return;
  // ✅ อนุญาตให้ซ้ำได้
  setSelected((prev) => [...prev, it]);
};


  const removeFromBuild = (idx) => setSelected((prev) => prev.filter((_, i) => i !== idx));
  const resetBuild = () => setSelected([]);

  const focusStatsLines = useMemo(() => {
    const s = focus?.stats || {};
    const keys = getNumericStatKeys(s);
    return keys.map((k) => ({
      key: k,
      label: STAT_LABELS?.[k] || k,
      value: +s[k] || 0,
    }));
  }, [focus]);

  return (
    <Card style={{ padding: 16 }}>
      {/* TOP BAR */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ fontWeight: "bold", color: "#ffd7dc" }}>Build Items</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ color: "#ffd7dc", fontWeight: "bold" }}>
            Total: {totalPrice.toLocaleString()} gold
          </div>
          <Button variant="danger" size="sm" onClick={resetBuild} disabled={selected.length === 0}>
            <RotateCcw size={12} /> Reset
          </Button>
        </div>
      </div>

      {/* BUILD SLOTS */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        {Array.from({ length: maxSlots }).map((_, i) => {
          const it = selected[i] || null;
          return (
            <div
              key={i}
              style={{
                width: 72,
                height: 72,
                borderRadius: 14,
                border: it ? "2px solid rgba(255,16,38,0.55)" : "2px solid rgba(255,16,38,0.2)",
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                cursor: it ? "pointer" : "default",
              }}
              onClick={() => it && setFocus(it)}
              title={it?.name || "Empty"}
            >
              {it ? (
                <>
                  <ItemThumb item={it} getItemImageCandidates={getItemImageCandidates} size={60} frame={true} />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromBuild(i);
                    }}
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      border: "none",
                      background: "rgba(0,0,0,0.6)",
                      color: "#ff1026",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </>
              ) : (
                <div style={{ color: "#6b7280", fontWeight: "bold" }}>{i + 1}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* MAIN LAYOUT: Category | Items by Tier | Detail */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(200px, 240px) minmax(360px, 1fr) minmax(260px, 320px)",
          gap: 12,
          alignItems: "start",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* LEFT: CATEGORY */}
        <Card style={{ padding: 12 }}>
          <div style={{ fontWeight: "bold", color: "#ffd7dc", marginBottom: 10 }}>Category</div>
          <div style={{ display: "grid", gap: 8 }}>
            {cats.map((c) => {
              const active = c === cat;
              return (
                <button
                  key={c}
                  onClick={() => {
                    setCat(c);
                    setFocus(null);
                  }}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: active ? "2px solid rgba(255,16,38,0.6)" : "2px solid rgba(255,16,38,0.2)",
                    background: active
                      ? "linear-gradient(135deg, rgba(255,16,38,0.25), rgba(0,0,0,0.55))"
                      : "rgba(0,0,0,0.55)",
                    color: active ? "#fff" : "#9ca3af",
                    cursor: "pointer",
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: "bold",
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 12 }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item..." icon={<Search size={14} />} />
          </div>
        </Card>

        {/* CENTER: ITEM LIST BY TIER */}
        <Card style={{ padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontWeight: "bold", color: "#ffd7dc" }}>{cat}</div>
            <div style={{ color: "#6b7280" }}>{filtered.length.toLocaleString()} items</div>
          </div>

          <div
            className="scroll-container"
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.max(1, byTier.length)}, minmax(260px, 1fr))`,
              gap: 10,
              maxHeight: 520,
              overflow: "auto",
              paddingRight: 6,
            }}
          >
            {byTier.map((group) => (
              <div key={group.tier} style={{ borderRadius: 12, border: "1px solid rgba(255,16,38,0.2)", overflow: "hidden" }}>
                <div
                  style={{
                    padding: "10px 12px",
                    background: "linear-gradient(to right, rgba(255,16,38,0.18), rgba(0,0,0,0.85))",
                    borderBottom: "1px solid rgba(255,16,38,0.2)",
                    fontWeight: "bold",
                    color: "#ffd7dc",
                    letterSpacing: "0.08em",
                  }}
                >
                  Tier {group.tier}
                </div>

                {group.items.map((it) => {
                  const active = focus?.name === it.name;
                  const st = statusMap.get(it.name);
                  const badgeColor =
                    st === "NEW" ? "#22c55e" : st === "REMOVED" ? "#ef4444" : st === "CHANGED" ? "#ff1026" : "#6b7280";

                  return (
                    <button
                      key={it.name}
                      type="button"
                      onClick={() => setFocus(it)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        border: "none",
                        borderTop: "1px solid rgba(255,16,38,0.12)",
                        background: active ? "rgba(255,16,38,0.15)" : "rgba(0,0,0,0.0)",
                        cursor: "pointer",
                        color: "#fff",
                        textAlign: "left",
                        fontFamily: "'Rajdhani', sans-serif",
                      }}
                    >
                      <ItemThumb item={it} getItemImageCandidates={getItemImageCandidates} size={40} frame={true} />

                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 }}>
                          {it.name}
                          {st && <Badge color={badgeColor} size="xs">{st}</Badge>}
                        </div>
                        <div style={{ color: "#9ca3af", fontSize: 13 }}>
                          {(+it.price || 0).toLocaleString()} gold
                        </div>
                      </div>

                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToBuild(it);
                        }}
                        disabled={selected.length >= maxSlots || selected.some((x) => x.name === it.name)}
                        style={{ padding: "10px 14px" }}
                      >
                        Add
                      </Button>
                    </button>
                  );
                })}

                {group.items.length === 0 && <div style={{ padding: 12, color: "#9ca3af" }}>No items</div>}
              </div>
            ))}

            {byTier.length === 0 && <div style={{ padding: 12, color: "#9ca3af" }}>No item found</div>}
          </div>
        </Card>

        {/* RIGHT: DETAIL */}
        <Card style={{ padding: 12 }}>
          <div style={{ fontWeight: "bold", color: "#ffd7dc", marginBottom: 10 }}>Details</div>

          {!focus ? (
            <div style={{ color: "#9ca3af" }}>คลิกไอเท็มตรงกลางเพื่อดูรายละเอียด</div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
                <ItemThumb item={focus} getItemImageCandidates={getItemImageCandidates} size={64} frame={true} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 18 }}>
                    {focus.name}
                  </div>
                  <div style={{ color: "#9ca3af" }}>
                    {focus.category || "Unknown"} • Tier {focus.tier || 1} • {(+focus.price || 0).toLocaleString()} gold
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                {focusStatsLines.length === 0 ? (
                  <div style={{ color: "#9ca3af" }}>ไม่มีสเตตัสในข้อมูล</div>
                ) : (
                  focusStatsLines.map((x) => (
                    <div key={x.key} style={{ display: "flex", justifyContent: "space-between", color: "#9ca3af" }}>
                      <span style={{ color: "#ffd7dc", fontWeight: "bold" }}>{x.label}</span>
                      <span style={{ fontFamily: "monospace", fontWeight: "bold", color: "#fff" }}>
                        {x.value > 0 ? "+" : ""}{Math.round(x.value).toLocaleString()}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <div style={{ borderTop: "1px solid rgba(255,16,38,0.2)", paddingTop: 10 }}>
                <div style={{ color: "#ffd7dc", fontWeight: "bold", marginBottom: 6 }}>Passive</div>
                <div style={{ color: "#9ca3af", whiteSpace: "pre-wrap", lineHeight: 1.35 }}>
                  {focus.passive || "—"}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                <Button
                  onClick={() => addToBuild(focus)}
                  disabled={selected.length >= maxSlots || selected.some((x) => x.name === focus.name)}
                >
                  Equip
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Card>
  );
};

const HeroBuildComparisonTab = ({
  patchA,
  patchB,
  getHeroImageUrl,
  getItemImageCandidates,
  HeroSelector,
}) => {
  const [heroName, setHeroName] = useState("");
  const [level, setLevel] = useState(15);

  // ✅ ใช้ร้าน BuildItemShop → เก็บเป็น item objects (เหมือน ItemShopPicker ใน MetaLab)
  const [selectedBuild, setSelectedBuild] = useState([]);

  const heroesA = useMemo(() => patchA?.heroes || [], [patchA]);
  const heroesB = useMemo(() => patchB?.heroes || [], [patchB]);
  const itemsA = useMemo(() => patchA?.items || [], [patchA]);
  const itemsB = useMemo(() => patchB?.items || [], [patchB]);

  // ✅ รวม hero จาก A+B (ให้ patch ใหม่ B ทับ A ถ้าชื่อซ้ำ) แล้ว sort
  const mergedHeroes = useMemo(() => {
    const map = new Map();
    for (const h of heroesB) if (h?.name) map.set(h.name, h);
    for (const h of heroesA) if (h?.name && !map.has(h.name)) map.set(h.name, h);
    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [heroesA, heroesB]);

  // หา hero ในแต่ละ patch
  const heroA = useMemo(() => heroesA.find((h) => h.name === heroName), [heroesA, heroName]);
  const heroB = useMemo(() => heroesB.find((h) => h.name === heroName), [heroesB, heroName]);

  // ✅ map selectedBuild(object) → item ของแต่ละ patch ตามชื่อ
  const buildItemsA = useMemo(
    () => selectedBuild.map((x) => itemsA.find((it) => it.name === x.name)).filter(Boolean),
    [selectedBuild, itemsA]
  );
  const buildItemsB = useMemo(
    () => selectedBuild.map((x) => itemsB.find((it) => it.name === x.name)).filter(Boolean),
    [selectedBuild, itemsB]
  );

  // คำนวณ total stats
  const statsA = useMemo(
    () => (heroA ? computeTotalStatsRealisticRealistic(getHeroLevelObj(heroA, level), buildItemsA) : null),
    [heroA, level, buildItemsA]
  );
  const statsB = useMemo(
    () => (heroB ? computeTotalStatsRealisticRealistic(getHeroLevelObj(heroB, level), buildItemsB) : null),
    [heroB, level, buildItemsB]
  );

  const statKeys = ["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed"];

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Controls */}
      <Card neon style={{ padding: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
          <Users size={18} color="#ff1026" />
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "16px" }}>
            HERO BUILD COMPARISON
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px" }}>
          {/* ✅ ใช้ HeroSelector ของ MetaLab ถ้ามี */}
          {HeroSelector ? (
            <HeroSelector
              heroes={mergedHeroes}
              value={heroName}
              onChange={(e) => setHeroName(e.target.value)}
              label="Hero"
              getHeroImageUrl={getHeroImageUrl}
              showRoleFilter={true}
              maxHeight={400}
            />
          ) : (
            // fallback: ถ้าไม่ได้ส่ง HeroSelector มา
            <Select
              value={heroName}
              onChange={(e) => setHeroName(e.target.value)}
              options={[
                { value: "", label: "Select Hero..." },
                ...mergedHeroes.map((h) => ({ value: h.name, label: h.name })),
              ]}
              icon={<Users size={14} />}
            />
          )}

          <Select
            value={level}
            onChange={(e) => setLevel(+e.target.value)}
            options={[...Array(15)].map((_, i) => ({ value: i + 1, label: `Level ${i + 1}` }))}
          />
        </div>
      </Card>

      {/* ✅ Item Shop Layout (เหมือน MetaLab) */}
      <BuildItemShop
        itemsA={itemsA}
        itemsB={itemsB}
        selected={selectedBuild}
        setSelected={setSelectedBuild}
        getItemImageCandidates={getItemImageCandidates}
      />

      {/* Comparison Cards */}
      {heroName && (heroA || heroB) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <HeroBuildCard
            hero={heroA}
            items={buildItemsA}
            level={level}
            patch={patchA}
            patchLabel={patchA?.meta?.patchName || "Patch A"}
            getHeroImageUrl={getHeroImageUrl}
            getItemImageCandidates={getItemImageCandidates}
            color="#ef4444"
          />
          <HeroBuildCard
            hero={heroB}
            items={buildItemsB}
            level={level}
            patch={patchB}
            patchLabel={patchB?.meta?.patchName || "Patch B"}
            getHeroImageUrl={getHeroImageUrl}
            getItemImageCandidates={getItemImageCandidates}
            color="#22c55e"
          />
        </div>
      )}

      {/* Stats Diff */}
      {statsA && statsB && (
        <Card style={{ padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <BarChart3 size={16} color="#ff1026" />
            <span style={{ fontWeight: "bold", color: "#ffd7dc", fontSize: "13px" }}>
              TOTAL STATS DIFFERENCE
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" }}>
            {statKeys.map((stat) => (
              <StatDiffCard
                key={stat}
                stat={stat}
                oldValue={statsA.total?.[stat] || 0}
                newValue={statsB.total?.[stat] || 0}
              />
            ))}
          </div>

          {/* Summary */}
          <div
            style={{
              marginTop: "16px",
              padding: "16px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(255,16,38,0.15), rgba(0,0,0,0.8))",
              border: "1px solid rgba(255,16,38,0.3)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", textAlign: "center" }}>
              <div>
                <div style={{ color: "#6b7280", fontSize: "10px", marginBottom: "4px" }}>Total HP Change</div>
                <DiffIndicator value={(statsB.total.hp || 0) - (statsA.total.hp || 0)} showZero size="lg" />
              </div>
              <div>
                <div style={{ color: "#6b7280", fontSize: "10px", marginBottom: "4px" }}>Total ATK Change</div>
                <DiffIndicator value={(statsB.total.phys_atk || 0) - (statsA.total.phys_atk || 0)} showZero size="lg" />
              </div>
              <div>
                <div style={{ color: "#6b7280", fontSize: "10px", marginBottom: "4px" }}>Total AP Change</div>
                <DiffIndicator value={(statsB.total.magic_power || 0) - (statsA.total.magic_power || 0)} showZero size="lg" />
              </div>
              <div>
                <div style={{ color: "#6b7280", fontSize: "10px", marginBottom: "4px" }}>Build Cost</div>
                <DiffIndicator
                  value={
                    buildItemsB.reduce((s, it) => s + (it?.price || 0), 0) -
                    buildItemsA.reduce((s, it) => s + (it?.price || 0), 0)
                  }
                  showZero
                  size="lg"
                />
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};



// =====================================================================
// MAIN COMPONENT: EnhancedPatchCompareV2
// =====================================================================

export const EnhancedPatchCompareV2 = ({
  patches,
  aIdx,
  bIdx,
  setAIdx,
  setBIdx,
  getHeroImageUrl,
  getItemImageCandidates,
  HeroSelector, // ✅ รับ component จาก MetaLab
}) => {

  const [level, setLevel] = useState(15);
  const [tab, setTab] = useState("overview");
  const [heroSearch, setHeroSearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [filterRole, setFilterRole] = useState("ALL");
  const [filterCategory, setFilterCategory] = useState("ALL");
  const [expandedHeroes, setExpandedHeroes] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [showOnlyChanged, setShowOnlyChanged] = useState(true);

  const canCompare = patches.length >= 2 && patches[aIdx] && patches[bIdx] && aIdx !== bIdx;
  const patchA = patches[aIdx];
  const patchB = patches[bIdx];

  // Calculate diffs
  const heroDiff = useMemo(() => {
    if (!canCompare) return { added: [], removed: [], changed: [], all: [] };
    const heroesA = patchA.heroes || [];
    const heroesB = patchB.heroes || [];
    const mapA = new Map(heroesA.map((h) => [h.name, h]));
    const mapB = new Map(heroesB.map((h) => [h.name, h]));

    const added = [];
    const removed = [];
    const changed = [];
    const statKeys = ["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed", "armor_pierce_pct", "attack_speed", "crit_rate", "life_steal", "cooldown_reduction"];

    for (const [name, hB] of mapB) {
      if (!mapA.has(name)) {
        added.push({ name, role: hB.role, heroA: null, heroB: hB });
        continue;
      }
      const hA = mapA.get(name);
      const lvlA = getHeroLevelObj(hA, level) || {};
      const lvlB = getHeroLevelObj(hB, level) || {};
      const diffs = [];
      let totalChange = 0;

      for (const f of statKeys) {
        const vA = lvlA[f] ?? 0;
        const vB = lvlB[f] ?? 0;
        if (vA !== vB) {
          diffs.push({ field: f, old: vA, new: vB, diff: vB - vA });
          totalChange += Math.abs(vB - vA);
        }
      }
      if (hA.role !== hB.role) {
        diffs.push({ field: "role", old: hA.role, new: hB.role, diff: null });
      }
      if (diffs.length > 0) {
        changed.push({ name, role: hB.role, diffs, totalChange, heroA: hA, heroB: hB });
      }
    }

    for (const [name, hA] of mapA) {
      if (!mapB.has(name)) {
        removed.push({ name, role: hA.role, heroA: hA, heroB: null });
      }
    }

    return { added, removed, changed, all: [...added, ...removed, ...changed] };
  }, [canCompare, patchA, patchB, level]);

  const itemDiff = useMemo(() => {
    if (!canCompare) return { added: [], removed: [], changed: [], all: [] };
    const itemsA = patchA.items || [];
    const itemsB = patchB.items || [];
    const mapA = new Map(itemsA.map((it) => [it.name, it]));
    const mapB = new Map(itemsB.map((it) => [it.name, it]));

    const added = [];
    const removed = [];
    const changed = [];
    const statKeys = ["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed", "armor_pierce_pct", "attack_speed", "crit_rate", "life_steal", "cooldown_reduction"];

    for (const [name, itB] of mapB) {
      if (!mapA.has(name)) {
        added.push({ name, category: itB.category, itemA: null, itemB: itB });
        continue;
      }
      const itA = mapA.get(name);
      const diffs = [];
      let totalChange = 0;

      if (itA.price !== itB.price) {
        const d = (itB.price || 0) - (itA.price || 0);
        diffs.push({ field: "price", old: itA.price, new: itB.price, diff: d });
        totalChange += Math.abs(d);
      }

      const sA = itA.stats || {};
      const sB = itB.stats || {};
      for (const f of statKeys) {
        const vA = sA[f] || 0;
        const vB = sB[f] || 0;
        if (vA !== vB) {
          diffs.push({ field: f, old: vA, new: vB, diff: vB - vA });
          totalChange += Math.abs(vB - vA);
        }
      }
      if ((itA.passive || "") !== (itB.passive || "")) {
        diffs.push({ field: "passive", old: itA.passive, new: itB.passive, diff: null });
      }
      if (diffs.length > 0) {
        changed.push({ name, category: itB.category, diffs, totalChange, itemA: itA, itemB: itB });
      }
    }

    for (const [name, itA] of mapA) {
      if (!mapB.has(name)) {
        removed.push({ name, category: itA.category, itemA: itA, itemB: null });
      }
    }

    return { added, removed, changed, all: [...added, ...removed, ...changed] };
  }, [canCompare, patchA, patchB]);

  // Filters
  const roles = useMemo(() => ["ALL", ...new Set((patchB?.heroes || []).map((h) => h.role).filter(Boolean))], [patchB]);
  const categories = useMemo(() => ["ALL", ...new Set((patchB?.items || []).map((it) => it.category).filter(Boolean))], [patchB]);

  const filteredHeroes = useMemo(() => {
    let list = showOnlyChanged ? heroDiff.changed : [...heroDiff.added, ...heroDiff.changed, ...heroDiff.removed];
    if (heroSearch.trim()) {
      list = list.filter((h) => h.name.toLowerCase().includes(heroSearch.toLowerCase()));
    }
    if (filterRole !== "ALL") {
      list = list.filter((h) => h.role === filterRole);
    }
    return list.sort((a, b) => (b.totalChange || 0) - (a.totalChange || 0));
  }, [heroDiff, heroSearch, filterRole, showOnlyChanged]);

  const filteredItems = useMemo(() => {
    let list = showOnlyChanged ? itemDiff.changed : [...itemDiff.added, ...itemDiff.changed, ...itemDiff.removed];
    if (itemSearch.trim()) {
      list = list.filter((it) => it.name.toLowerCase().includes(itemSearch.toLowerCase()));
    }
    if (filterCategory !== "ALL") {
      list = list.filter((it) => it.category === filterCategory);
    }
    return list.sort((a, b) => (b.totalChange || 0) - (a.totalChange || 0));
  }, [itemDiff, itemSearch, filterCategory, showOnlyChanged]);

  const toggleHeroExpand = (name) => {
    setExpandedHeroes((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleItemExpand = (name) => {
    setExpandedItems((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const options = patches.map((p, i) => ({ value: i, label: `${i + 1}. ${p.meta?.patchName || `Patch ${i + 1}`}` }));

  // Not enough patches
  if (!canCompare) {
    return (
      <Card neon style={{ padding: "40px", textAlign: "center" }}>
        <GitCompare size={60} color="#ff1026" style={{ marginBottom: "20px" }} />
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "20px", marginBottom: "12px" }}>
          PATCH COMPARE V2
        </div>
        <div style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Select two different patches to compare
        </div>
        <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
          <div style={{ minWidth: "200px" }}>
            <div style={{ color: "#6b7280", fontWeight: "bold", marginBottom: "6px", fontSize: "11px" }}>BEFORE (OLD)</div>
            <Select value={aIdx} onChange={(e) => setAIdx(+e.target.value)} options={options} />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: "10px" }}>
            <ChevronRight size={24} color="#ff1026" />
          </div>
          <div style={{ minWidth: "200px" }}>
            <div style={{ color: "#6b7280", fontWeight: "bold", marginBottom: "6px", fontSize: "11px" }}>AFTER (NEW)</div>
            <Select value={bIdx} onChange={(e) => setBIdx(+e.target.value)} options={options} />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Header */}
      <Card neon style={{ padding: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #ff1026, #a30013)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GitCompare size={24} color="#fff" />
            </div>
            <div>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "18px" }}>PATCH COMPARE V2</div>
              <div style={{ color: "#6b7280", fontSize: "12px" }}>Advanced comparison with build analysis</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <Select value={aIdx} onChange={(e) => setAIdx(+e.target.value)} options={options} style={{ minWidth: "160px" }} />
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ff1026, #a30013)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ChevronRight size={20} color="#fff" />
            </div>
            <Select value={bIdx} onChange={(e) => setBIdx(+e.target.value)} options={options} style={{ minWidth: "160px" }} />
            <Select
              value={level}
              onChange={(e) => setLevel(+e.target.value)}
              options={[...Array(15)].map((_, i) => ({ value: i + 1, label: `Lv ${i + 1}` }))}
              style={{ width: "90px" }}
            />
          </div>
        </div>

        {/* Stats Summary */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "12px", marginTop: "20px" }}>
          {[
            { label: "Heroes Added", value: heroDiff.added.length, icon: Plus, color: "#22c55e" },
            { label: "Heroes Removed", value: heroDiff.removed.length, icon: X, color: "#ef4444" },
            { label: "Heroes Changed", value: heroDiff.changed.length, icon: Activity, color: "#ff1026" },
            { label: "Items Added", value: itemDiff.added.length, icon: Plus, color: "#22c55e" },
            { label: "Items Removed", value: itemDiff.removed.length, icon: X, color: "#ef4444" },
            { label: "Items Changed", value: itemDiff.changed.length, icon: Activity, color: "#ff1026" },
          ].map((stat, i) => (
            <Card key={i} style={{ padding: "12px", textAlign: "center" }} glow={stat.value > 0 ? stat.color : null}>
              <stat.icon size={16} color={stat.color} style={{ marginBottom: "6px" }} />
              <div style={{ fontSize: "24px", fontWeight: "bold", color: stat.color, fontFamily: "'Orbitron', sans-serif" }}>
                {stat.value}
              </div>
              <div style={{ fontSize: "10px", color: "#6b7280" }}>{stat.label}</div>
            </Card>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} icon={Layers}>
          Overview
        </TabButton>
        <TabButton active={tab === "heroes"} onClick={() => setTab("heroes")} icon={Users}>
          Heroes ({heroDiff.changed.length})
        </TabButton>
        <TabButton active={tab === "items"} onClick={() => setTab("items")} icon={Package}>
          Items ({itemDiff.changed.length})
        </TabButton>
        <TabButton active={tab === "build"} onClick={() => setTab("build")} icon={Calculator}>
          Build Compare
        </TabButton>
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
  <WinnersLosersSection
    heroDiff={heroDiff}
    itemDiff={itemDiff}
    level={level}
    getHeroImageUrl={getHeroImageUrl}
    getItemImageCandidates={getItemImageCandidates}
  />
)}


      {/* Heroes Tab */}
      {tab === "heroes" && (
        <Card style={{ padding: "16px" }}>
          {/* Filters */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
            <Input
              value={heroSearch}
              onChange={(e) => setHeroSearch(e.target.value)}
              placeholder="Search heroes..."
              icon={<Search size={14} />}
              style={{ flex: 1, minWidth: "200px" }}
            />
            <Select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              options={roles.map((r) => ({ value: r, label: r }))}
              style={{ width: "140px" }}
            />
            <Button
              variant={showOnlyChanged ? "primary" : "secondary"}
              onClick={() => setShowOnlyChanged(!showOnlyChanged)}
              size="sm"
            >
              <Filter size={12} />
              {showOnlyChanged ? "Changed Only" : "Show All"}
            </Button>
          </div>

          {/* Hero List */}
          <div className="scroll-container" style={{ maxHeight: "600px", overflow: "auto", display: "grid", gap: "10px" }}>
            {filteredHeroes.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                <Users size={40} style={{ marginBottom: "12px", opacity: 0.5 }} />
                <div>No heroes match your filters</div>
              </div>
            ) : (
              filteredHeroes.map((h) => (
                <HeroComparisonCard
                  key={h.name}
                  heroA={h.heroA}
                  heroB={h.heroB}
                  level={level}
                  expanded={expandedHeroes[h.name]}
                  onToggle={() => toggleHeroExpand(h.name)}
                  getHeroImageUrl={getHeroImageUrl}   // ✅ pass
                />
              ))
            )}
          </div>
        </Card>
      )}

      {/* Items Tab */}
      {tab === "items" && (
        <Card style={{ padding: "16px" }}>
          {/* Filters */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
            <Input
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Search items..."
              icon={<Search size={14} />}
              style={{ flex: 1, minWidth: "200px" }}
            />
            <Select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              options={categories.map((c) => ({ value: c, label: c }))}
              style={{ width: "140px" }}
            />
            <Button
              variant={showOnlyChanged ? "primary" : "secondary"}
              onClick={() => setShowOnlyChanged(!showOnlyChanged)}
              size="sm"
            >
              <Filter size={12} />
              {showOnlyChanged ? "Changed Only" : "Show All"}
            </Button>
          </div>

          {/* Item List */}
          <div className="scroll-container" style={{ maxHeight: "600px", overflow: "auto", display: "grid", gap: "10px" }}>
            {filteredItems.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                <Package size={40} style={{ marginBottom: "12px", opacity: 0.5 }} />
                <div>No items match your filters</div>
              </div>
            ) : (
              filteredItems.map((it) => (
                <ItemComparisonCard
                  key={it.name}
                  itemA={it.itemA}
                  itemB={it.itemB}
                  expanded={expandedItems[it.name]}
                  onToggle={() => toggleItemExpand(it.name)}
                  getItemImageCandidates={getItemImageCandidates} // ✅ pass
                />
              ))
            )}
          </div>
        </Card>
      )}

      {/* Build Compare Tab */}
      {tab === "build" && (
  <HeroBuildComparisonTab
    patchA={patchA}
    patchB={patchB}
    getHeroImageUrl={getHeroImageUrl}
    getItemImageCandidates={getItemImageCandidates}
    HeroSelector={HeroSelector} // ✅ ส่งต่อ
  />
)}
    </div>
  );
};

export default EnhancedPatchCompareV2;
